import React, { createContext, useState, useEffect, useContext } from 'react';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  sendEmailVerification
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// User roles
export const ROLES = {
  ADMIN: 'admin',      // Full access - can read, write, delete, and manage users
  EDITOR: 'editor',    // Can read and write data but not delete or manage users
  VIEWER: 'viewer'     // Read-only access
};

// Access levels for specific functions
export const ACCESS_LEVELS = {
  READ: 'read',
  EDIT: 'edit',
  DELETE: 'delete',
  MANAGE_USERS: 'manage_users'
};

// Define role permissions
export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [ACCESS_LEVELS.READ, ACCESS_LEVELS.EDIT, ACCESS_LEVELS.DELETE, ACCESS_LEVELS.MANAGE_USERS],
  [ROLES.EDITOR]: [ACCESS_LEVELS.READ, ACCESS_LEVELS.EDIT],
  [ROLES.VIEWER]: [ACCESS_LEVELS.READ]
};

// Module permissions - which role can access which module
export const MODULE_PERMISSIONS = {
  // Company module
  'company-setup': [ROLES.ADMIN, ROLES.EDITOR],
  'manage-company-setup': [ROLES.ADMIN, ROLES.EDITOR],

  // Salary calculations
  'salary-calculator': [ROLES.ADMIN, ROLES.EDITOR, ROLES.VIEWER],
  'manage-monthly-calculations': [ROLES.ADMIN, ROLES.EDITOR],
  'lead-info-form': [ROLES.ADMIN, ROLES.EDITOR, ROLES.VIEWER],
  'manage-lead-salary': [ROLES.ADMIN, ROLES.EDITOR],
  'recorder-info-form': [ROLES.ADMIN, ROLES.EDITOR, ROLES.VIEWER],
  'manage-recorder-salary': [ROLES.ADMIN, ROLES.EDITOR],
  'excavator-info-form': [ROLES.ADMIN, ROLES.EDITOR, ROLES.VIEWER],
  'manage-excavator-salary': [ROLES.ADMIN, ROLES.EDITOR],

  // Operations
  'diesel-monthly': [ROLES.ADMIN, ROLES.EDITOR, ROLES.VIEWER],
  'truck-operations': [ROLES.ADMIN, ROLES.EDITOR, ROLES.VIEWER],
  'truck-operations/records': [ROLES.ADMIN, ROLES.EDITOR],
  'truck-monthly': [ROLES.ADMIN, ROLES.EDITOR, ROLES.VIEWER],

  // User management - admin only
  'user-management': [ROLES.ADMIN]
};

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const auth = getAuth();

  // Check if a user has permission for a specific action
  const hasPermission = (actionType) => {

    if (!userRole) return false;
    return ROLE_PERMISSIONS[userRole]?.includes(actionType) || false;
  };

  // Check if user can access a specific module
  const canAccessModule = (modulePath) => {
    if (!userRole) return false;
    return MODULE_PERMISSIONS[modulePath]?.includes(userRole) || false;
  };

  // Sign in user
  const signIn = async (email, password) => {
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      if (!userCredential.user.emailVerified) {
        // Optional: Sign out and throw an error
        await signOut(auth);
        throw new Error('Please verify your email before logging in');
      }

      // Update Firestore user document with verification status
      await updateDoc(doc(db, 'user_roles', userCredential.user.uid), {
        emailVerified: true
      });

      return userCredential.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Sign out user
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setCurrentUser(null);
      setUserRole(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Create a new user (admin only function)
  const createUser = async (email, password, role) => {
    if (userRole !== ROLES.ADMIN) {
      throw new Error('Only administrators can create new users');
    }

    try {
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Immediately sign out the newly created user
      await firebaseSignOut(auth);

      // Send email verification
      await sendEmailVerification(user);

      // Store user role in Firestore
      await setDoc(doc(db, 'user_roles', user.uid), {
        email: user.email,
        role: role,
        createdBy: currentUser.uid,
        createdAt: new Date(),
        emailVerified: false
      });

      return user;

    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Fetch user role from Firestore
  const getUserRole = async (uid) => {
    try {
      const userRoleDoc = await getDoc(doc(db, 'user_roles', uid));
      if (userRoleDoc.exists()) {
        return userRoleDoc.data().role;
      }
      return null;
    } catch (err) {
      console.error('Error fetching user role:', err);
      return null;
    }
  };

  const resendVerificationEmail = async () => {
    const user = auth.currentUser;

    if (!user) {
      throw new Error('No user is currently logged in');
    }

    try {
      await sendEmailVerification(user);
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        // Fetch user role
        const role = await getUserRole(user.uid);

        setUserRole(role);
      } else {
        setUserRole(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, [auth]);

  const value = {
    currentUser,
    userRole,
    loading,
    error,
    signIn,
    signOut,
    createUser,
    hasPermission,
    canAccessModule,
    ROLES,
    ACCESS_LEVELS,
    resendVerificationEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;